import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [readingChapter, setReadingChapter] = useState<number | null>(null);

  const comic = id ? comics.find((c) => c.id === id) : undefined;
  const category = comic ? categories.find((cat) => cat.slug === comic.categorySlug) : undefined;

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <SEOHead
          title="漫画未找到 - yy漫画网"
          description="抱歉，您查找的漫画不存在或已被移除。"
          keywords="yy漫画网,韩漫免费漫画"
          canonicalUrl="/"
        />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex flex-col items-center justify-center px-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-error-warning-line text-2xl text-foreground-400" />
          </div>
          <h1 className="text-xl font-medium text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">抱歉，您查找的漫画不存在或已被移除。</p>
          <Link
            to="/"
            className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const descToShow = showFullDesc ? comic.description : comic.description.slice(0, 80) + '...';

  const formatViews = (views: number) => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const relatedComics = comics.filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id).slice(0, 4);

  const detailSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yymanhua.com' },
          { '@type': 'ListItem', position: 2, name: comic.category, item: `https://yymanhua.com/category/${comic.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: comic.title, item: `https://yymanhua.com/comic/${comic.id}` },
        ],
      },
      {
        '@type': 'Article',
        headline: `${comic.title} - 韩漫免费漫画在线阅读`,
        description: comic.description,
        author: { '@type': 'Person', name: comic.author },
        datePublished: comic.chapters[0]?.updateDate,
        dateModified: comic.updateDate,
        image: comic.cover,
        publisher: { '@type': 'Organization', name: 'yy漫画网' },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://yymanhua.com/comic/${comic.id}` },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${comic.title} - ${comic.author} - 韩漫免费漫画在线阅读 - yy漫画网`}
        description={`${comic.description.slice(0, 120)} yy漫画网提供${comic.title}免费下拉式在线阅读，${comic.category}类韩漫精品。`}
        keywords={`${comic.title},${comic.author},${comic.category},${comic.tags.join(',')},韩漫免费漫画,yy漫画网`}
        canonicalUrl={`/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        schemaJson={detailSchema}
      />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
              {comic.category}
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-900 font-medium truncate">{comic.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-[280px] flex-shrink-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                  title={`${comic.title} - 韩漫免费漫画`}
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">{comic.title}</h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-4">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line" />
                  {comic.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-folder-line" />
                  <Link to={`/category/${comic.categorySlug}`} className="text-primary-500 hover:text-primary-600 cursor-pointer transition-colors">
                    {comic.category}
                  </Link>
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500" />
                  {comic.rating}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" />
                  {formatViews(comic.views)} 阅读
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-5">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100/70 rounded-lg p-4 mb-6">
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {descToShow}
                </p>
                {comic.description.length > 80 && (
                  <button
                    onClick={() => setShowFullDesc(!showFullDesc)}
                    className="text-xs text-primary-500 hover:text-primary-600 mt-1 cursor-pointer whitespace-nowrap"
                  >
                    {showFullDesc ? '收起' : '展开全部'}
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3 mb-6">
                <Link
                  to={`/comic/${comic.id}`}
                  className="px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    setReadingChapter(comic.chapters[0]?.number ?? null);
                  }}
                >
                  开始阅读
                </Link>
                <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                  <i className="ri-bookmark-line" />
                </button>
              </div>
            </div>
          </div>

          {readingChapter && (
            <div className="mt-8 bg-background-100/70 rounded-xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl text-foreground-900">
                  第{readingChapter}话 - {comic.chapters.find((ch) => ch.number === readingChapter)?.title}
                </h2>
                <button
                  onClick={() => setReadingChapter(null)}
                  className="text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer flex items-center gap-1 whitespace-nowrap"
                >
                  <i className="ri-close-line" /> 关闭阅读
                </button>
              </div>
              <div className="bg-background-50 rounded-lg p-10 md:p-14 text-center">
                <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                  <i className="ri-book-open-line text-3xl text-primary-500" />
                </div>
                <h3 className="text-lg font-medium text-foreground-900 mb-2">下拉式阅读模式</h3>
                <p className="text-sm text-foreground-500 max-w-md mx-auto leading-relaxed">
                  正在加载第{readingChapter}话内容。在正式版中，漫画将通过下拉式滚动无缝加载所有页面，为你提供流畅的阅读体验。
                </p>
              </div>
            </div>
          )}

          <div className="mt-10">
            <h2 className="font-heading text-xl text-foreground-900 mb-4">章节列表</h2>
            <div className="bg-background-100/70 rounded-xl p-4 md:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {comic.chapters.map((chapter) => (
                  <button
                    key={chapter.id}
                    onClick={() => setReadingChapter(chapter.number)}
                    className={`flex items-center justify-between p-3 rounded-lg text-left cursor-pointer transition-colors whitespace-nowrap ${
                      readingChapter === chapter.number
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-background-50 hover:bg-primary-50 text-foreground-700'
                    }`}
                  >
                    <div className="min-w-0">
                      <span className="text-sm font-medium">第{chapter.number}话</span>
                      <span className="text-xs text-foreground-500 ml-2 truncate">{chapter.title}</span>
                    </div>
                    <span className="text-[10px] text-foreground-400 flex-shrink-0 ml-2">{chapter.pageCount}P</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-12 mb-8">
              <h2 className="font-heading text-xl text-foreground-900 mb-4">同类推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}